/**
 * PublicLayout - Modern layout za javne stranice (auth, contact, classes, terms, itd.)
 */

"use client";

import { Box, Divider, Flex, useDisclosure } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { type PropsWithChildren, type ReactNode, useMemo, useEffect } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypeAdmin, PageTypeGlobal, PageTypePortal } from "@/lib/types";
import { ThemeToggle } from "@/components/ui";
import { PublicFooter } from "./PublicFooter";
import { PublicNavbar } from "./PublicNavbar";
import { PublicMobileMenuDrawer } from "./PublicMobileMenuDrawer";
import { PublicAuthTrustStrip } from "./PublicAuthTrustStrip";
import { PublicGlassPanel } from "./PublicGlassPanel";
import { PublicPageHero } from "./PublicPageHero";
import { PublicEntrance } from "@/components/shared/motion";
import {
  PUBLIC_AUTH_FORM_WIDTH,
  PUBLIC_CONTENT_SPACING,
  PUBLIC_FORM_MAX_WIDTH,
  PUBLIC_NAVBAR_WIDTH,
} from "./public-layout-constants";
import { hexToRgba } from "@/lib/utils/color";
import { InitialPersonalizationProvider } from "@/lib/theme/InitialPersonalizationContext";
import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { HeroVisibilityProvider, useHeroVisibility } from "@/lib/contexts";
import type { PublicTicketsNavLink } from "@/features/tickets/public/public-tickets-nav";
import { PublicLocationsNavProvider } from "@/hooks/shared/usePublicLocationsNavLink";
import { PublicTicketsNavProvider } from "@/hooks/shared/usePublicTicketsNavLink";
import type { PublicLocationsNavLink } from "@/lib/utils/locationsPublic";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { FULL_VIEWPORT_HEIGHT } from "@/lib/layout/viewport";

type LayoutType = "auth" | "contact" | "wide" | "centered" | "small-form";

const getLayoutType = (pathname: string): LayoutType => {
  if (
    pathname === PageTypePortal.Login ||
    pathname === PageTypePortal.SignUp ||
    pathname === PageTypePortal.ForgotPassword ||
    pathname === PageTypeAdmin.SignIn
  ) {
    return "auth";
  }

  if (pathname === PageTypeGlobal.Contact) {
    return "contact";
  }

  if (
    pathname === PageTypePortal.ResetPassword ||
    pathname === "/forbidden" ||
    pathname === "/403"
  ) {
    return "small-form";
  }

  if (
    pathname === PageTypeGlobal.Home ||
    pathname === PageTypeGlobal.Terms ||
    pathname.startsWith(PageTypeGlobal.Classes) ||
    pathname === PageTypeGlobal.Teachers ||
    pathname.startsWith(`${PageTypeGlobal.Teachers}/`) ||
    pathname === PageTypeGlobal.Locations ||
    pathname.startsWith(`${PageTypeGlobal.Locations}/`) ||
    pathname === PageTypeGlobal.Tickets ||
    pathname.startsWith(`${PageTypeGlobal.Tickets}/`)
  ) {
    return "wide";
  }

  return "centered";
};

type PublicLayoutProps = PropsWithChildren & {
  hideNavbarLinks?: boolean;
  locationsNav?: PublicLocationsNavLink | null;
  ticketsNav?: PublicTicketsNavLink | null;
  initialPersonalization?: {
    colorPalette: ColorPaletteId;
    customColor: string | null;
    grainEnabled: boolean;
  } | null;
};

const PublicLayoutContent = ({
  children,
  hideNavbarLinks = false,
}: Omit<PublicLayoutProps, "initialPersonalization">) => {
  const pathname = usePathname();
  const theme = useThemeTokens();
  const { isHeroVisible } = useHeroVisibility();
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } =
    useDisclosure();

  useEffect(() => {
    onMenuClose();
  }, [pathname, onMenuClose]);

  const layoutType = useMemo(() => getLayoutType(pathname), [pathname]);
  const isAuthLayout = layoutType === "auth";
  const isContactLayout = layoutType === "contact";
  const isWideLayout = layoutType === "wide";
  const isSmallForm = layoutType === "small-form";

  const tenantId = resolveEffectiveTenantNumber();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: true },
  );

  const heroContent = useMemo(() => {
    if (!isHeroVisible) return null;
    if (!isAuthLayout) return null;

    if (pathname === PageTypePortal.SignUp) {
      return {
        label: contentSettings.signUpLabel,
        title: contentSettings.signUpTitle,
        description: contentSettings.signUpDescription,
      };
    }
    if (pathname === PageTypePortal.ForgotPassword) {
      return {
        label: contentSettings.forgotPasswordLabel,
        title: contentSettings.forgotPasswordTitle,
        description: contentSettings.forgotPasswordDescription,
      };
    }
    if (pathname === PageTypeAdmin.SignIn) {
      return {
        label: contentSettings.adminSignInLabel,
        title: contentSettings.adminSignInTitle,
        description: contentSettings.adminSignInDescription,
      };
    }
    return {
      label: contentSettings.signInLabel,
      title: contentSettings.signInTitle,
      description: contentSettings.signInDescription,
    };
  }, [pathname, isAuthLayout, contentSettings, isHeroVisible]);

  const renderAuthLayout = () => {
    const maxW =
      pathname === PageTypePortal.SignUp ? PUBLIC_NAVBAR_WIDTH : PUBLIC_AUTH_FORM_WIDTH;

    return (
      <PublicGlassPanel maxW={maxW}>
        {heroContent ? (
          <>
            <Box textAlign={{ base: "left", md: "center" }} maxW="640px" mx="auto">
              <PublicPageHero {...heroContent} />
            </Box>
            <Divider
              my={{ base: 5, md: 6 }}
              borderColor={theme.borderGlass.subtle}
              opacity={0.7}
            />
          </>
        ) : null}
        {children}
        {isHeroVisible ? (
          <PublicEntrance variant="subtle" delay={0.2}>
            <PublicAuthTrustStrip />
          </PublicEntrance>
        ) : null}
      </PublicGlassPanel>
    );
  };

  const renderMainContent = (): ReactNode => {
    if (isAuthLayout) {
      return renderAuthLayout();
    }

    if (isContactLayout || isWideLayout) {
      return (
        <PublicGlassPanel maxW={PUBLIC_NAVBAR_WIDTH}>
          <Flex direction="column" gap={PUBLIC_CONTENT_SPACING.gap}>
            {children}
          </Flex>
        </PublicGlassPanel>
      );
    }

    if (isSmallForm) {
      return (
        <PublicGlassPanel maxW={PUBLIC_FORM_MAX_WIDTH}>
          <Flex direction="column" gap={PUBLIC_CONTENT_SPACING.gap}>
            {children}
          </Flex>
        </PublicGlassPanel>
      );
    }

    return (
      <PublicGlassPanel maxW={PUBLIC_NAVBAR_WIDTH}>
        <Flex direction="column" gap={PUBLIC_CONTENT_SPACING.gap}>
          {children}
        </Flex>
      </PublicGlassPanel>
    );
  };

  return (
    <Flex
      w="100%"
      minH={FULL_VIEWPORT_HEIGHT}
      direction="column"
      position="relative"
      bg={theme.bg.primary}
      overflow="hidden"
    >
      {theme.gradientEnabled ? (
        <Box
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          pointerEvents="none"
          zIndex={0}
          sx={{
            background:
              theme.mode === "dark"
                ? `radial-gradient(ellipse 70% 45% at 50% 0%, ${hexToRgba(theme.accent.primary, 0.12)} 0%, transparent 55%),
                   linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0.16) 100%)`
                : `radial-gradient(ellipse 70% 45% at 50% 0%, ${hexToRgba(theme.accent.primary, 0.06)} 0%, transparent 55%),
                   linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)`,
          }}
        />
      ) : null}

      {theme.mode === "dark" ? (
        <Box
          position="absolute"
          top="6%"
          left="50%"
          w="320px"
          h="320px"
          borderRadius="50%"
          bg={hexToRgba(theme.accent.primary, 0.09)}
          filter="blur(80px)"
          pointerEvents="none"
          zIndex={0}
          transform="translateX(-50%)"
        />
      ) : null}

      <Box
        display="none"
        position="fixed"
        top="20px"
        right="20px"
        zIndex={11}
        sx={{
          "@media (min-width: 1201px)": {
            display: "block",
          },
        }}
      >
        <ThemeToggle />
      </Box>

      <PublicNavbar
        hideLinks={hideNavbarLinks}
        isMenuOpen={isMenuOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
      />

      {!hideNavbarLinks ? (
        <PublicMobileMenuDrawer isOpen={isMenuOpen} onClose={onMenuClose} />
      ) : null}

      <Flex
        as="main"
        id="main-content"
        flex="1"
        w="100%"
        position="relative"
        zIndex={1}
        px={PUBLIC_CONTENT_SPACING.px}
        pt={PUBLIC_CONTENT_SPACING.pt}
        pb={PUBLIC_CONTENT_SPACING.pb}
        align="flex-start"
        justify="center"
      >
        {renderMainContent()}
      </Flex>

      <PublicFooter />
    </Flex>
  );
};

export const PublicLayout = ({
  children,
  hideNavbarLinks = false,
  locationsNav,
  ticketsNav,
  initialPersonalization,
}: PublicLayoutProps) => {
  const navValue = hideNavbarLinks ? null : locationsNav;
  const ticketsNavValue = hideNavbarLinks ? null : ticketsNav;

  return (
    <InitialPersonalizationProvider value={initialPersonalization}>
      <PublicLocationsNavProvider value={navValue}>
        <PublicTicketsNavProvider value={ticketsNavValue}>
          <HeroVisibilityProvider>
            <PublicLayoutContent hideNavbarLinks={hideNavbarLinks}>
              {children}
            </PublicLayoutContent>
          </HeroVisibilityProvider>
        </PublicTicketsNavProvider>
      </PublicLocationsNavProvider>
    </InitialPersonalizationProvider>
  );
};
